import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ConfirmModal from "../popup/MainPopUp";
import { makeCreateMilestone, makeDeleteMilestone } from "../../pages/Api";
import LoaderOverlay from "../loading/GlobalLoader";
import { FileText, Trash } from "lucide-react";
import { MilestonePdfGenerator } from "./MilestonePdf";
import { FaChevronDown, FaFilePdf } from "react-icons/fa";
import { MilestonePdfGeneratorDunes } from "./MilestonePdfDuna";

export const Milestones = ({ invoiceId, milestonesData, fetchData, data }) => {
    const [milestones, setMilestones] = useState([]);
    const [isOpen, setIsOpen] = useState(false);
    const [newMilestone, setNewMilestone] = useState({ name: "", percentage: "" });
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [enableRetention, setEnableRetention] = useState(false);
    const [enableAdvance, setEnableAdvance] = useState(false);
    const [retentionPercentage, setRetentionPercentage] = useState("");
    const [advancePercentage, setAdvancePercentage] = useState("");
    const totalAllocatedPercentage = milestones.reduce((sum, m) => sum + m.percentage, 0);
    const maxAllowedPercentage = 100 - totalAllocatedPercentage;
    const isOverAllocated = parseFloat(newMilestone.percentage) > maxAllowedPercentage;
    const [expandedMilestoneId, setExpandedMilestoneId] = useState(null);

    const toggleExpand = (milestoneId) => {
        setExpandedMilestoneId((prevId) => (prevId === milestoneId ? null : milestoneId));
    };

    const handleAddRow = () => {
        if (!newMilestone.name.trim() || isNaN(parseFloat(newMilestone.percentage)) || parseFloat(newMilestone.percentage) <= 0 || isOverAllocated) {
            return;
        }
        setMilestones([...milestones, { ...newMilestone, percentage: parseFloat(newMilestone.percentage) }]);
        setNewMilestone({ name: "", percentage: "" });
    };

    const handleDelete = (index) => {
        const updatedMilestones = [...milestones];
        updatedMilestones.splice(index, 1);
        setMilestones(updatedMilestones);
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            await makeCreateMilestone(invoiceId, milestones, advancePercentage ? advancePercentage : 0, retentionPercentage ? retentionPercentage : 0);
            fetchData();
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
            setIsOpen(false);
        }
    };
    const statusValues = [
        { value: "Pending", label: "Pending" },
        { value: "in_progress", label: "In Progress" },
        { value: "completed", label: "Completed" }
    ];

    const handleDeleteAll = async (token) => {
        // Simulate delete action (you may replace this with an API call)
        try {
            const response = await makeDeleteMilestone(invoiceId, token)
            fetchData();
        } catch (error) {
            console.log(error)
        } finally {
            setShowDeleteModal(false);
        }
        // milestonesData.length = 0;
        //  // Refresh the milestones data
    };

    return (
        <>
            <LoaderOverlay isLoading={loading} />
            <motion.div className="mt-8 relative bg-white p-6 rounded-lg shadow-xl">
                {milestonesData?.length > 0 && (
                    // Delete Button with Enhanced Hover Animation
                    <motion.button
                        className="absolute top-4 right-4 p-3 rounded-full bg-gradient-to-r from-red-500 to-pink-600 text-white shadow-lg"
                        onClick={() => setShowDeleteModal(true)}
                        whileHover={{ scale: 1.2, rotate: 10 }}
                        whileTap={{ scale: 0.9 }}
                    >
                        <Trash />
                    </motion.button>

                )}
                <h3 className="text-3xl font-extrabold text-gray-800 mb-6 text-center">Milestones</h3>

                {/* Existing Milestones */}
                <div className="space-y-4">
                    {milestonesData?.length > 0 ? (
                        milestonesData.map((milestone) => (
                            <motion.div
                                key={milestone.id}
                                whileHover={{ scale: 1.02 }}
                                className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-100 to-blue-300 rounded-xl shadow-lg border border-blue-400 transition-all duration-300"
                            >
                                {/* Milestone Info - Adjusts Width when Expanded */}
                                <div className={`flex items-center transition-all duration-300 ${expandedMilestoneId === milestone.id ? "w-3/4" : "w-full"}`}>
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold text-gray-800">{milestone.name}</h3>
                                    </div>
                                    <div className="flex-1 text-center">
                                        <span className="text-md font-medium text-blue-800">
                                            {statusValues.find(option => option.value === milestone.status)?.label || milestone.status}
                                        </span>
                                    </div>
                                    <div className="flex-1 text-right">
                                        <span className="px-4 py-2 text-sm font-bold text-white bg-blue-600 rounded-full shadow-md">
                                            {milestone.percentage}%
                                        </span>
                                    </div>
                                </div>

                                {/* PDF Download Section (Expands Left) */}
                                <motion.div
                                    className={`flex items-center ml-5 gap-2 transition-all duration-300 ${expandedMilestoneId === milestone.id ? "w-auto" : "w-auto"}`}
                                >
                                    {/* Expanding Buttons for PDF Download */}
                                    <AnimatePresence>
                                        {expandedMilestoneId === milestone.id && (
                                            <>
                                                <motion.button
                                                    initial={{ opacity: 0, x: 20 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    exit={{ opacity: 0, x: 20 }}
                                                    onClick={() => {
                                                        MilestonePdfGenerator(data, milestone);
                                                        setExpandedMilestoneId(null);
                                                    }}
                                                    className="flex items-center gap-2 px-4 py-2 text-blue-700 bg-blue-100 rounded-md hover:bg-blue-200 transition-all"
                                                >
                                                    <FaFilePdf size={18} />
                                                    Trigon
                                                </motion.button>
                                                <motion.button
                                                    initial={{ opacity: 0, x: 20 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    exit={{ opacity: 0, x: 20 }}
                                                    onClick={() => {
                                                        MilestonePdfGeneratorDunes(data, milestone);
                                                        setExpandedMilestoneId(null);
                                                    }}
                                                    className="flex items-center gap-2 px-4 py-2 text-green-700 bg-green-100 rounded-md hover:bg-green-200 transition-all"
                                                >
                                                    <FaFilePdf size={18} />
                                                    Dunes
                                                </motion.button>
                                            </>
                                        )}
                                    </AnimatePresence>

                                    {/* PDF Icon (Always on the Right) */}
                                    <button
                                        className="flex items-center gap-2 text-blue-700 bg-white p-2 rounded-lg shadow-md border border-blue-300 hover:bg-blue-600 hover:text-white transition-all duration-300"
                                        onClick={() => toggleExpand(milestone.id)}
                                    >
                                        <FileText size={20} />
                                    </button>
                                </motion.div>
                            </motion.div>
                        ))
                    ) : (
                        <p className="text-gray-500 text-center italic">No milestones added yet.</p>
                    )}
                </div>
                {/* Hide "Manage Milestones" button if milestonesData is not empty */}
                {milestonesData?.length === 0 && (
                    <button
                        className="mt-6 w-full py-4 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold rounded-xl shadow-lg hover:scale-[1.02] transition-transform"
                        onClick={() => setIsOpen(true)}
                    >
                        ➕ Manage Milestones
                    </button>
                )}

                <AnimatePresence>
                    {isOpen && (
                        <motion.div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50">
                            <motion.div className="bg-white p-10 rounded-2xl shadow-2xl w-[650px] relative max-h-[85vh] overflow-y-auto">
                                <motion.button className="absolute top-4 right-4 text-gray-600 hover:text-red-500 transition transform hover:rotate-90" onClick={() => setIsOpen(false)}>✖</motion.button>
                                <h3 className="text-3xl font-bold mb-6 text-gray-800 text-center">🎯 Manage Milestones</h3>

                                {/* Retention and Advance Checkboxes */}
                                <div className="p-6 bg-white rounded-xl shadow-md border border-gray-200">
                                    {/* Checkboxes */}
                                    <div className="flex items-center space-x-6 mb-4">
                                        <label className="flex items-center space-x-2 cursor-pointer hover:scale-105 transition-transform">
                                            <input
                                                type="checkbox"
                                                checked={enableRetention}
                                                onChange={() => setEnableRetention(!enableRetention)}
                                                className="w-5 h-5 accent-blue-500"
                                            />
                                            <span className="text-gray-700 font-semibold text-lg">Retention</span>
                                        </label>
                                        <label className="flex items-center space-x-2 cursor-pointer hover:scale-105 transition-transform">
                                            <input
                                                type="checkbox"
                                                checked={enableAdvance}
                                                onChange={() => setEnableAdvance(!enableAdvance)}
                                                className="w-5 h-5 accent-green-500"
                                            />
                                            <span className="text-gray-700 font-semibold text-lg">Advance</span>
                                        </label>
                                    </div>

                                    {/* Input Fields */}
                                    <AnimatePresence>
                                        {(enableRetention || enableAdvance) && (
                                            <motion.div
                                                initial={{ opacity: 0, y: -10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                exit={{ opacity: 0, y: -10 }}
                                                transition={{ duration: 0.3 }}
                                                className="p-5 border border-gray-300 rounded-xl shadow-lg bg-gray-50 space-y-4"
                                            >
                                                {enableRetention && (
                                                    <motion.div
                                                        initial={{ opacity: 0, x: -10 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        exit={{ opacity: 0, x: -10 }}
                                                        transition={{ duration: 0.3, delay: 0.1 }}
                                                        className="flex flex-col"
                                                    >
                                                        <label className="text-gray-600 font-semibold mb-1">
                                                            Retention Percentage
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className="border border-gray-300 p-3 rounded-lg w-full focus:ring-2 focus:ring-blue-400 shadow-sm transition-all duration-200"
                                                            placeholder="Max 100%"
                                                            value={retentionPercentage}
                                                            onChange={(e) => setRetentionPercentage(Math.min(100, e.target.value))}
                                                        />
                                                    </motion.div>
                                                )}
                                                {enableAdvance && (
                                                    <motion.div
                                                        initial={{ opacity: 0, x: 10 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        exit={{ opacity: 0, x: 10 }}
                                                        transition={{ duration: 0.3, delay: 0.2 }}
                                                        className="flex flex-col"
                                                    >
                                                        <label className="text-gray-600 font-semibold mb-1">
                                                            Advance Percentage
                                                        </label>
                                                        <input
                                                            type="number"
                                                            className="border border-gray-300 p-3 rounded-lg w-full focus:ring-2 focus:ring-green-400 shadow-sm transition-all duration-200"
                                                            placeholder="Max 100%"
                                                            value={advancePercentage}
                                                            onChange={(e) => setAdvancePercentage(Math.min(100, e.target.value))}
                                                        />
                                                    </motion.div>
                                                )}
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>


                                <div className="space-y-4">
                                    {milestones.map((milestone, index) => (
                                        <div key={index} className="flex items-center bg-gray-100 p-5 rounded-lg shadow space-x-4">
                                            <input type="text" className="border p-4 rounded-lg w-3/5 text-lg" value={milestone.name} onChange={(e) => {
                                                const updatedMilestones = [...milestones];
                                                updatedMilestones[index].name = e.target.value;
                                                setMilestones(updatedMilestones);
                                            }} />
                                            <input type="number" className="border p-4 rounded-lg w-1/4 text-lg" value={milestone.percentage} onChange={(e) => {
                                                const updatedMilestones = [...milestones];
                                                updatedMilestones[index].percentage = parseFloat(e.target.value);
                                                setMilestones(updatedMilestones);
                                            }} />
                                            <button onClick={() => handleDelete(index)} className="text-red-500 hover:text-red-700">✖</button>
                                        </div>
                                    ))}
                                </div>

                                <div className="flex items-center mt-6 p-5 bg-gray-50 rounded-lg shadow space-x-4">
                                    <input type="text" className="border p-4 rounded-lg w-3/5 text-lg" placeholder="Milestone Name" value={newMilestone.name} onChange={(e) => setNewMilestone({ ...newMilestone, name: e.target.value })} />
                                    <input onWheel={(e) => e.preventDefault()} type="number" className="border no-arrows p-4 rounded-lg w-1/4 text-lg placeholder:text-sm" placeholder={`% (Max ${maxAllowedPercentage}%)`} value={newMilestone.percentage} onChange={(e) => setNewMilestone({ ...newMilestone, percentage: e.target.value })} />
                                    <motion.button onClick={handleAddRow} className="text-green-500 hover:text-green-700 transform hover:scale-110">➕</motion.button>
                                </div>

                                <p className={`text-sm text-center mt-4 ${isOverAllocated ? "text-red-600" : "text-gray-600"}`}>
                                    Maximum percentage you can allocate: <strong>{maxAllowedPercentage}%</strong>
                                </p>

                                <motion.button
                                    disabled={milestones?.length <= 0}
                                    className="w-full mt-6 py-4 bg-gradient-to-r from-green-500 to-green-700 text-white font-bold rounded-lg shadow-lg hover:scale-105 transition-transform"
                                    onClick={() => setShowModal(true)}
                                >
                                    Save
                                </motion.button>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                <ConfirmModal
                    isOpen={showModal}
                    onClose={() => setShowModal(false)}
                    onConfirm={handleSave}
                    title="Confirm Save"
                    message="Are you sure you want to save these milestones? Once saved, you won’t be able to edit them."
                    confirmText="Yes, Save"
                    cancelText="Cancel"
                />
                <ConfirmModal
                    isOpen={showDeleteModal}
                    onClose={() => setShowDeleteModal(false)}
                    onConfirm={handleDeleteAll}
                    title="Confirm Delete"
                    message="Are you sure you want to delete all milestones?"
                    confirmText="Yes, Delete"
                    cancelText="Cancel"
                    warning={true}
                    requiresSecretKey={milestonesData?.some(milestone => milestone.status !== "Pending")}
                />
            </motion.div>
        </>
    );
};
